import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type OrgRole = 'owner' | 'admin' | 'manager' | 'member';

export interface OrgMembership {
  orgId: string;
  orgName: string;
  orgSlug: string;
  role: OrgRole;
  department: string | null;
  status: string;
}

const ROLE_RANK: Record<OrgRole, number> = { owner: 4, admin: 3, manager: 2, member: 1 };
const ACTIVE_ORG_STORAGE_KEY = 'vovy-active-org-id';

export function useOrganizationService() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_ORG_STORAGE_KEY)
  );

  const { data, isLoading } = useQuery({
    queryKey: ['my-organizations', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_organizations');
      if (error) throw error;
      return (data ?? []).map((row): OrgMembership => ({
        orgId: row.org_id,
        orgName: row.org_name,
        orgSlug: row.org_slug,
        role: row.role as OrgRole,
        department: row.department,
        status: row.status,
      }));
    },
    enabled: !!user,
  });

  const organizations = useMemo(() => (data ?? []).filter((o) => o.status === 'active'), [data]);

  useEffect(() => {
    if (!activeOrgId && organizations.length > 0) {
      setActiveOrgId(organizations[0].orgId);
    }
  }, [activeOrgId, organizations]);

  const activeOrg = useMemo(
    () => organizations.find((o) => o.orgId === activeOrgId) ?? organizations[0] ?? null,
    [organizations, activeOrgId]
  );

  const setActiveOrg = (orgId: string) => {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
    setActiveOrgId(orgId);
  };

  const hasRole = (minRole: OrgRole) => {
    if (!activeOrg) return false;
    return ROLE_RANK[activeOrg.role] >= ROLE_RANK[minRole];
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['my-organizations', user?.id] });
  };

  return { loading: isLoading, organizations, activeOrg, setActiveOrg, hasRole, refresh };
}
